import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { api } from './lib/api.js';
import { startWebSocket, useStore } from './lib/store.js';
import Layout from './components/Layout.js';
import Login from './pages/Login.js';
import TwoFactorChallenge from './pages/TwoFactorChallenge.js';
import { loadAuthedConfig, loadPublicBranding } from './lib/branding.js';
import Dashboard from './pages/Dashboard.js';
import ServerDetail from './pages/ServerDetail.js';
import MachineDetail from './pages/MachineDetail.js';
import NewServer from './pages/NewServer.js';
import Settings from './pages/Settings.js';
import Backups from './pages/Backups.js';
import Bulk from './pages/Bulk.js';
import Files from './pages/Files.js';

interface Me {
  discord_id: string;
  discord_username: string;
  role: 'admin' | 'operator' | 'viewer';
  avatar_url?: string;
}

export default function App() {
  const [auth, setAuth] = useState<{ status: 'loading' | 'in' | 'out'; me: Me | null }>({
    status: 'loading',
    me: null,
  });

  useEffect(() => {
    api.get<Me>('/api/auth/me')
      .then((me) => {
        setAuth({ status: 'in', me });
        startWebSocket();
        // Authenticated users get the full panel config (admin can edit).
        loadAuthedConfig().catch(() => {});
      })
      .catch(() => {
        setAuth({ status: 'out', me: null });
        // Login page needs the branding too.
        loadPublicBranding().catch(() => {});
      });
  }, []);

  if (auth.status === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-fg-muted text-sm">Loading…</div>
      </div>
    );
  }

  if (auth.status === 'out') {
    return (
      <Routes>
        <Route path="/login/2fa" element={<TwoFactorChallenge />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <Layout user={auth.me!}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/servers/new" element={<NewServer />} />
        <Route path="/servers/:id" element={<ServerDetail />} />
        <Route path="/machines/:name" element={<MachineDetail />} />
        <Route path="/backups" element={<Backups />} />
        <Route path="/bulk" element={<Bulk me={auth.me!} />} />
        <Route path="/files" element={<Files me={auth.me!} />} />
        <Route path="/settings" element={<Settings me={auth.me!} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

/** Tiny shim for connection status indicator in topbar */
export function ConnectionDot() {
  const connected = useStore((s) => s.connected);
  return (
    <span
      title={connected ? 'WebSocket connected' : 'WebSocket disconnected — reconnecting…'}
      className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-status-online' : 'bg-status-error animate-pulse'}`}
    />
  );
}
